import { Overlay } from '@blueprintjs/core'
import classNames from 'classnames'
import React from 'react'

import { TetherTarget } from 'browser/components/atomic-elements/atoms/tether-target'
import { useUI2Platform } from 'browser/components/ui2/forms/ui2-form/ui2-platform-context'

import './_ui2-unit-picker.scss'

export type UI2UnitPickerOption = { label: string; value: string }

interface IUI2UnitPickerProps {
  options: UI2UnitPickerOption[]
  value: string | undefined
  onChange: (value: string) => void
  isDisabled?: boolean
}

// Anchor the popover to the bottom-right of the trigger so the menu's right
// edge tracks the chevron — the trigger always sits in the right addon slot,
// so right-aligned placement keeps the menu on-screen for narrow fields.
const DW_TETHER_OPTIONS = {
  attachment: 'top right',
  targetAttachment: 'bottom right',
}

/**
 * Unit picker for UI2 quantity-type fields. Replaces the native <select> so
 * the popup anchors reliably to the trigger across browsers.
 *
 *   - desktopWeb: tethered popover menu (TetherTarget).
 *   - mobileWeb: bottom sheet (BlueprintJS Overlay), matching DateTimeInput.
 */
export const UI2UnitPicker: React.FC<IUI2UnitPickerProps> = ({
  options, value, onChange, isDisabled,
}) => {
  const platform = useUI2Platform()
  const [isSheetOpen, setSheetOpen] = React.useState(false)

  const selected = options.find((o) => o.value === value) ?? options[0]

  const handleSelect = (next: string): void => {
    onChange(next)
    setSheetOpen(false)
  }

  const trigger = (
    <button
      type='button'
      disabled={isDisabled}
      className={classNames('ui2-fieldControl-interactive ui2-unitPicker-trigger', {
        'ui2-fieldControl-interactive--disabled': isDisabled,
      })}
      onClick={platform === 'mobileWeb' && !isDisabled ? () => setSheetOpen(true) : undefined}
    >
      <span className='ui2-unitPicker-label'>{selected?.label}</span>
      <span className='ui2-unitPicker-chevron' aria-hidden='true' />
    </button>
  )

  if (platform === 'mobileWeb') {
    return (
      <>
        {trigger}
        <UI2UnitPickerSheet
          isOpen={isSheetOpen}
          onClose={() => setSheetOpen(false)}
          options={options}
          selectedValue={selected?.value}
          onSelect={handleSelect}
        />
      </>
    )
  }

  return (
    <TetherTarget
      openOnClick={!isDisabled}
      closeOnEsc={true}
      tetherOptions={DW_TETHER_OPTIONS}
      tethered={(
        <UI2UnitPickerMenu
          options={options}
          selectedValue={selected?.value}
          onSelect={handleSelect}
        />
      )}
    >
      {trigger}
    </TetherTarget>
  )
}

interface IUI2UnitPickerMenuProps {
  options: UI2UnitPickerOption[]
  selectedValue: string | undefined
  onSelect: (value: string) => void
  // Injected by TetherTarget — see tether-target/index.tsx.
  onClose?: () => void
}

const UI2UnitPickerMenu: React.FC<IUI2UnitPickerMenuProps> = ({
  options, selectedValue, onSelect, onClose,
}) => {
  const handle = (v: string): void => {
    onSelect(v)
    onClose?.()
  }
  return (
    <div className='ui2-unitPicker-menu' role='listbox'>
      {options.map((opt) => (
        <button
          key={opt.value}
          type='button'
          role='option'
          aria-selected={opt.value === selectedValue}
          className={classNames('ui2-unitPicker-menuItem', {
            'ui2-unitPicker-menuItem--selected': opt.value === selectedValue,
          })}
          onClick={() => handle(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

interface IUI2UnitPickerSheetProps {
  isOpen: boolean
  onClose: () => void
  options: UI2UnitPickerOption[]
  selectedValue: string | undefined
  onSelect: (value: string) => void
}

const UI2UnitPickerSheet: React.FC<IUI2UnitPickerSheetProps> = ({
  isOpen, onClose, options, selectedValue, onSelect,
}) => (
  <Overlay
    isOpen={isOpen}
    onClose={onClose}
    canEscapeKeyClose={true}
    canOutsideClickClose={true}
    hasBackdrop={true}
    className='ui2-unitPicker-sheetOverlay'
    backdropClassName='ui2-unitPicker-sheetBackdrop'
  >
    <div className='ui2-unitPicker-sheet' role='dialog' aria-label='Select unit'>
      <div className='ui2-unitPicker-sheetHeader'>
        <button type='button' className='ui2-unitPicker-sheetCancel' onClick={onClose}>
          Cancel
        </button>
        <span className='ui2-unitPicker-sheetTitle'>Select unit</span>
        <span className='ui2-unitPicker-sheetSpacer' aria-hidden='true' />
      </div>
      <div className='ui2-unitPicker-sheetContent' role='listbox'>
        {options.map((opt) => (
          <button
            key={opt.value}
            type='button'
            role='option'
            aria-selected={opt.value === selectedValue}
            className={classNames('ui2-unitPicker-sheetOption', {
              'ui2-unitPicker-sheetOption--selected': opt.value === selectedValue,
            })}
            onClick={() => onSelect(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  </Overlay>
)
